import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BriefcaseBusiness, LoaderCircle, RefreshCw, ArrowLeft, Search, Eye, Star, X } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { useAuth, formatApiError } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const STATUS_STYLE = {
  applied: "bg-[#22C55E]/10 text-[#22C55E]",
  viewed: "bg-[#7C3AED]/15 text-[#A78BFA]",
  shortlisted: "bg-[#06B6D4]/10 text-[#06B6D4]",
  rejected: "bg-white/5 text-slate-500",
};

const ALLOWED = ["recruiter", "hr", "admin"];

export default function RecruiterMode() {
  const { user, checked } = useAuth();
  const [apps, setApps] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/applications/all`, headers());
      setApps(data.applications);
    } catch {
      setApps([]);
    }
  }, []);

  useEffect(() => {
    if (!user || !ALLOWED.includes(user.role)) return;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user, load]);

  const setStatus = async (appId, status) => {
    setBusyId(appId + status);
    try {
      await axios.patch(`${API}/applications/${appId}/status`, { status }, headers());
      toast.success(
        status === "shortlisted" ? "Candidate shortlisted — they got an alert 🎉" :
        status === "viewed" ? "Marked as viewed — candidate notified 👀" : "Application rejected"
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!apps) return null;
    const q = query.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter((a) =>
      [a.job, a.company, a.applicant.name, a.applicant.email, a.applicant.skills, a.applicant.college]
        .join(" ").toLowerCase().includes(q)
    );
  }, [apps, query]);

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B]">
        <LoaderCircle className="h-8 w-8 animate-spin text-[#06B6D4]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  if (!ALLOWED.includes(user.role)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#09090B] px-4 text-center text-white" data-testid="recruiter-denied">
        <BriefcaseBusiness className="h-10 w-10 text-[#F97316]" />
        <p className="mt-4 font-display text-xl font-extrabold">Recruiter access only</p>
        <p className="mt-2 max-w-sm text-sm text-slate-400">
          This board is for recruiter, HR and admin accounts. Your account is a {user.role} account.
        </p>
        <Link to="/app" className="mt-6 rounded-full border border-white/15 px-6 py-2.5 text-xs font-bold text-slate-200 hover:border-[#06B6D4]/60">
          ← Back to My FUNZ
        </Link>
      </div>
    );
  }

  const stats = [
    { label: "Total applicants", value: (apps || []).length },
    { label: "Awaiting review", value: (apps || []).filter((a) => a.status === "applied").length },
    { label: "Shortlisted", value: (apps || []).filter((a) => a.status === "shortlisted").length },
  ];

  return (
    <div data-testid="recruiter-mode" className="min-h-screen bg-[#09090B] text-white">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#09090B]/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 sm:px-6">
          <Link to="/" data-testid="recruiter-logo" className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-display text-base font-extrabold tracking-tight">
              FUN<span className="text-[#06B6D4]">Z</span>
              <span className="ml-2 rounded-full bg-[#7C3AED]/20 px-2.5 py-0.5 font-jbmono text-[9px] uppercase tracking-widest text-[#A78BFA]">
                Recruiter Board
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-2.5">
            <button
              data-testid="recruiter-refresh"
              onClick={load}
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-[#06B6D4]/60 hover:text-white"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <Link
              to="/app"
              data-testid="recruiter-back"
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> My FUNZ
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <h1 className="flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          <BriefcaseBusiness className="h-8 w-8 text-[#7C3AED]" /> Candidate Pipeline
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          Every view, shortlist or reject pings the candidate instantly. Refreshes every 15 seconds.
        </p>

        <div className="mt-8 grid grid-cols-3 gap-3">
          {stats.map((s, i) => (
            <div key={s.label} data-testid={`recruiter-stat-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
              <p className="font-display text-2xl font-extrabold">{s.value}</p>
              <p className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="relative mt-6">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="recruiter-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search candidates by name, skill, college, job…"
            className="w-full rounded-2xl border border-white/10 bg-white/[0.04] py-3.5 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#7C3AED]/60"
          />
        </div>

        <div className="mt-6 space-y-4" data-testid="recruiter-applications">
          {filtered === null ? (
            <LoaderCircle className="h-6 w-6 animate-spin text-[#06B6D4]" />
          ) : filtered.length === 0 ? (
            <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-sm text-slate-500">
              No applications match — candidates appear here the moment they apply.
            </p>
          ) : (
            filtered.map((a) => (
              <div
                key={a.id}
                data-testid={`application-${a.id.slice(-6)}`}
                className={`rounded-3xl border p-5 backdrop-blur-xl sm:p-6 ${
                  a.status === "shortlisted" ? "border-[#06B6D4]/30 bg-[#06B6D4]/[0.04]" : "border-white/10 bg-white/[0.03]"
                }`}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#06B6D4] font-display text-sm font-black">
                    {a.applicant.avatar || (a.applicant.name || a.applicant.email)[0].toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{a.applicant.name || a.applicant.email}</p>
                    <p className="truncate font-jbmono text-[10px] text-slate-500">{a.applicant.email}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[a.status]}`}>
                    {a.status}
                  </span>
                  <span className="ml-auto font-jbmono text-[11px] text-slate-500">
                    {String(a.created_at).slice(0, 16).replace("T", " ")}
                  </span>
                </div>
                <p className="mt-3 text-sm font-semibold">
                  {a.job} <span className="text-slate-400">@ {a.company}</span>
                </p>
                {(a.applicant.college || a.applicant.skills) && (
                  <p className="mt-1.5 font-jbmono text-[11px] text-slate-500">
                    {[a.applicant.college, a.applicant.course, a.applicant.year].filter(Boolean).join(" · ")}
                    {a.applicant.skills && ` · skills: ${a.applicant.skills}`}
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {a.status === "applied" && (
                    <button
                      data-testid={`view-${a.id.slice(-6)}`}
                      onClick={() => setStatus(a.id, "viewed")}
                      disabled={busyId}
                      className="flex items-center gap-1.5 rounded-full border border-[#7C3AED]/40 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-[#A78BFA] transition-colors hover:bg-[#7C3AED]/10 disabled:opacity-50"
                    >
                      <Eye className="h-3.5 w-3.5" /> Mark Viewed
                    </button>
                  )}
                  {!["shortlisted", "rejected"].includes(a.status) && (
                    <button
                      data-testid={`shortlist-${a.id.slice(-6)}`}
                      onClick={() => setStatus(a.id, "shortlisted")}
                      disabled={busyId}
                      className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
                    >
                      <Star className="h-3.5 w-3.5" /> Shortlist
                    </button>
                  )}
                  {!["shortlisted", "rejected"].includes(a.status) && (
                    <button
                      data-testid={`reject-${a.id.slice(-6)}`}
                      onClick={() => setStatus(a.id, "rejected")}
                      disabled={busyId}
                      className="flex items-center gap-1.5 rounded-full border border-white/15 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-slate-400 transition-colors hover:border-[#F97316]/60 hover:text-[#F97316] disabled:opacity-50"
                    >
                      <X className="h-3.5 w-3.5" /> Reject
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
